package dev.lingvo.site.components.studio.compose

import androidx.compose.runtime.Composable
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.site.components.localeLabel
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import dev.lingvo.site.components.studio.fonts.FontDownloadProgress
import org.jetbrains.compose.web.dom.Text

/** Export progress is shared across routes; failures link back to the exact design cell. */
@Composable
fun ComposeBar(
    store: ComposeRunStore, jobs: List<ComposeJob>, checkReport: RenderCheckReportDto?,
    canCompose: Boolean, onStart: () -> Unit,
) {
    val state = store.progress
    val total = checkReport?.let { it.renders.size + it.missing.size } ?: state.total.takeIf { it > 0 } ?: jobs.size
    val composed = checkReport?.let { composedCellCount(it, jobs, state.completedCells) } ?: state.done
    val remaining = (state.total - state.done).coerceAtLeast(0)
    val regionLabel = t("studio.compose.compose_bar.compose_screenshots", "Compose screenshots")
    Div(attrs = { attr("aria-label", regionLabel) }) {
        P { Text(trf("screenshots.compose.count", "Composed {done} of {total} · {outdated} out of date",
            "done" to composed, "total" to total, "outdated" to jobs.size)) }
        state.fontProgress?.let { FontDownloadProgress(it) }
        LTaskProgress(state.done, state.total, failed = state.failures.size, complete = !state.running,
            detail = state.current?.let { trf("screenshots.compose.current", "{locale} · {title} · {device}",
                "locale" to localeLabel(it.locale, ""), "title" to it.title, "device" to it.device.displayName) })
        if (state.running) {
            state.remainingSeconds?.let { seconds ->
                P { Text(trf("screenshots.compose.estimate", "About {minutes} minutes left", "minutes" to (seconds + 59) / 60)) }
            }
            P { Text(t("studio.compose.compose_bar.keep_this_tab_open_while_it", "Keep this tab open while it runs.")) }
            LButton(t("studio.compose.compose_bar.cancel", "Cancel"), { store.cancel() }, variant = LButtonVariant.SECONDARY)
        } else {
            val label = if (state.message != null) t("studio.compose.compose_bar.retry", "Retry")
                else if (state.cancelled) trf("screenshots.compose.remaining", "Compose the remaining {count}", "count" to remaining)
                else tPlural("screenshots.compose.start", jobs.size, "Compose {count} shot", "Compose {count} shots")
            LButton(label, onStart, enabled = canCompose && jobs.isNotEmpty())
        }
        if (state.cancelled) P {
            Text(trf("screenshots.compose.cancelled", "Composed {done} of {total}. The other {remaining} were not made.",
                "done" to state.done, "total" to state.total, "remaining" to remaining))
        }
        state.message?.let { message -> P(attrs = { attr("role", "alert") }) { Text(message) } }
        state.failures.forEach { failure ->
            val job = failure.job
            P {
                A(href = "design?slot=${encodeComposeSegment(job.slotId)}&locale=${encodeComposeSegment(job.locale)}&device=${job.device.id}&platform=${job.device.platform.id}") {
                    Text(trf("screenshots.compose.failure", "{locale} · {title}: {reason}",
                        "locale" to job.locale, "title" to job.title, "reason" to failure.reason))
                }
            }
        }
    }
}

private fun encodeComposeSegment(value: String): String = js("encodeURIComponent(value)") as String
